import { SelectChangeEvent } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { executeGetInterviews } from '../../dataAccess/webApi/dao/interviewsDao'
import { executeGetReservations } from '../../dataAccess/webApi/dao/reservationsDao'
import { executeGetLotteryReservations } from '../../dataAccess/webApi/dao/lotteryReservationsDao'
import { executeGetBatchResult } from '../../dataAccess/webApi/dao/acceptNoticeDao'
import { fromApiYmd, fromApiYmdHms } from '../../utils/dateUtil'
import { nullPropsToUndefined, undefinedPropsToNull } from '../../utils/objectUtil'
import { ChildrenChoice, getChildrenChoices } from '../common/child'
import { useErrorHandle } from '../common/error/errorHandler'
import { showLoading } from '../common/store/slices/application'
import { useOperationLog } from '../common/operationLog'
import { OperationId } from '../common/constant/operationLog'
import { yesNo, reservationStatusSearchCondition } from '../common/constant/classification'
import { selectSystemControl } from '../common/store/slices/systemControl'
import { executeGetProjectsFullHistories } from '../../dataAccess/webApi/dao/projectsDao'

interface LocationState {
  /** アクティブなお子さまIDの値 */
  childId?: string
  /** アクティブなタブの値 */
  tabValue?: string
  /** アクティブな利用予約の検索条件 */
  statusSearchCondition?: string
}

interface Reservation {
  reservationNo: string
  usageDate: Date
  useFromDatetime: Date
  useToDatetime: Date
  facilityName: string
  childName: string
  status: string
  isTodayConditionInput: string
  todayConditionInputName?: string | null
  todayConditionInputEndTime?: string | null
}
interface LotteryReservation {
  lotteryReservationNo: string
  usageDate: Date
  useFromDatetime: Date
  useToDatetime: Date
  facilityName: string
  childName: string
  status: string
}

interface Interview {
  interviewNo: string
  interviewDate?: Date
  interviewStartDatetime?: Date
  interviewEndDatetime?: Date
  facilityName: string
  childName: string
  status: string
  permitFlag: string
}

interface Project {
  projectId: string
  projectName: string
  projectCategoryId: string
  interviewMethodFlag: string
}

interface BatchResult {
  batchId: string
  reservationType: number
  reservationDate: string
  facilityId: string
  userId: string
  childId: string
  usageDates: string
  facilityName: string
  childName: string
}

interface PageState {
  activeChildId?: string
  activeTabValue: string
  /** 利用予約の検索条件 */
  activeStatusSearchCondition: string
  /** 利用予約 */
  reservations?: Reservation[]
  /** 抽選予約 */
  lotteryReservations?: LotteryReservation[]
  /** 面談予約 */
  interviews?: Interview[]
  /** 承諾通知書バッチ処理結果 */
  batchResults?: BatchResult[]

  childs: ChildrenChoice[]
  projects: Project[]
  tabs: typeof tabValue[keyof typeof tabValue][]

  acceptNoticeStatus: string
}

/** タブの選択値 */
export const tabValue = {
  reservation: '1',
  lotteryReservation: '2',
  interview: '3',
  acceptNotice: '4'
} as const

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const dispatch = useDispatch()
  const history = useHistory<LocationState | undefined>()
  const { addOperationLog } = useOperationLog()
  const sysCtrl = useSelector(selectSystemControl)
  const locationState = history.location.state

  const [state, setState] = useState<PageState>({
    activeChildId: locationState?.childId,
    activeTabValue: locationState?.tabValue ?? tabValue.reservation,
    activeStatusSearchCondition: locationState?.statusSearchCondition ?? reservationStatusSearchCondition.confirmedOnly,
    childs: [],
    projects: [],
    tabs: [],
    acceptNoticeStatus: ''
  })

  const load = useCallback(
    (
      isInitialize: boolean,
      loadedChilds: { value: string; label: string }[],
      activeStatusSearchCondition: string,
      activeTabValue: string,
      activeChildId?: string
    ) => {
      dispatch(
        showLoading({
          process: errorHandle(async () => {
            let callGetChildren
            let callGetReservations
            let callGetLotteryReservations
            let callGetInterviews
            let callGetAcceptNotice
            const callGetProjects = getProjectsMstFullHistories()
            if (isInitialize) {
              callGetChildren = getChildrenChoices()
            }
            switch (activeTabValue) {
              case (tabValue.reservation):
                callGetReservations = getReservations(activeStatusSearchCondition, activeChildId)
                break
              case (tabValue.lotteryReservation):
                callGetLotteryReservations = getLotteryReservations(activeChildId)
                break
              case (tabValue.interview):
                callGetInterviews = getInterviews(activeChildId)
                break
              case (tabValue.acceptNotice):
                callGetAcceptNotice = GetAcceptNotice(activeChildId)
                break
              default:
                console.log("wrong tab selection");
            }
            const [projects, childs, reservations, lotteryReservations, interviews, batchResults] = await Promise.all([
              callGetProjects ?? [],
              callGetChildren ?? loadedChilds,
              callGetReservations,
              callGetLotteryReservations,
              callGetInterviews,
              callGetAcceptNotice,
            ])
            const tabs: typeof tabValue[keyof typeof tabValue][] = Object.values(tabValue).filter(value => {
              if (value === tabValue.lotteryReservation || value === tabValue.acceptNotice) {
                // 抽選予約タブ, 帳票出力タブ
                return sysCtrl.lotteryDisplayFlag === yesNo.yes && sysCtrl.lotteryFlag === yesNo.yes
              }

              return true
            });

            const acceptNoticeStatus = sysCtrl.acceptNoticeStatus

            setState({
              activeChildId,
              activeTabValue,
              activeStatusSearchCondition,
              reservations,
              lotteryReservations,
              interviews,
              batchResults,
              childs,
              projects,
              tabs,
              acceptNoticeStatus,
            })
          }),
          isHiddenMain: isInitialize,
        })
      )
    },
    []
  )

  useEffect(() => {
    addOperationLog({ operationId: OperationId.OP_00000001 })

    load(true, state.childs, state.activeStatusSearchCondition, state.activeTabValue, state.activeChildId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // 戻るで表示した際にアクティブなタブなどを復元する為に履歴に保管
    history.replace({
      ...history.location,
      state: { childId: state.activeChildId, tabValue: state.activeTabValue, statusSearchCondition: state.activeStatusSearchCondition }
    })
  }, [state.activeChildId, state.activeTabValue, state.activeStatusSearchCondition])

  const changeChild = useCallback(
    (event: SelectChangeEvent<string>) => {
      addOperationLog({ operationId: OperationId.OP_00000028 })

      load(false, state.childs, state.activeStatusSearchCondition, state.activeTabValue, event.target.value)
    },
    [state, addOperationLog]
  )

  const changeTab = useCallback(
    (value: string) => {
      switch (value) {
        case (tabValue.reservation):
          addOperationLog({ operationId: OperationId.OP_00000042 })
          break
        case (tabValue.lotteryReservation):
          addOperationLog({ operationId: OperationId.OP_00000169 })
          break
        case (tabValue.interview):
          addOperationLog({ operationId: OperationId.OP_00000043 })
          break
        case (tabValue.acceptNotice):
          addOperationLog({ operationId: OperationId.OP_00000191 })
          break
        default:
          console.log("wrong tab selection");
      }
      load(false, state.childs, state.activeStatusSearchCondition, value, state.activeChildId)
    },
    [state, addOperationLog]
  )
  
  const statusSearchConditionChangeHandle = useCallback(
    (event: SelectChangeEvent<string>) => {
      load(false, state.childs, event.target.value, state.activeTabValue, state.activeChildId)
    },
    [state]
  )

  return {
    ...state,
    changeChild,
    changeTab,
    statusSearchConditionChangeHandle,
  }
}

/**
 * 事業マスタ（全履歴）を一覧取得して返す
 * @return {Array<Object>} - projectMstsのリスト
 */
const getProjectsMstFullHistories = async () => {
  const response = await executeGetProjectsFullHistories({})
  return response.result
}

const getReservations = async (activeStatusSearchCondition: string, childId?: string) => {
  const response = await executeGetReservations({ 
    ...undefinedPropsToNull({childId}),
    ...(activeStatusSearchCondition === reservationStatusSearchCondition.confirmedOnly && {confirmedOnlyGetFlag: yesNo.yes}),
  })
  return response.result.map((reservation) => {
    const { usageDate, useFromDatetime, useToDatetime, ...other } = reservation
    return nullPropsToUndefined({
      ...other,
      usageDate: fromApiYmd(usageDate),
      useFromDatetime: fromApiYmdHms(useFromDatetime),
      useToDatetime: fromApiYmdHms(useToDatetime),
    })
  })
}

const getLotteryReservations = async (childId?: string) => {
  const response = await executeGetLotteryReservations({childId})
  return response.result.map((reservation) => {
    const { usageDate, useFromDatetime, useToDatetime, ...other } = reservation
    return nullPropsToUndefined({
      ...other,
      usageDate: fromApiYmd(usageDate),
      useFromDatetime: fromApiYmdHms(useFromDatetime),
      useToDatetime: fromApiYmdHms(useToDatetime),
    })
  })
}

const getInterviews = async (childId?: string) => {
  const response = await executeGetInterviews(undefinedPropsToNull({ childId }))
  return response.result.map((interview) => {
    const { interviewDate, interviewStartDatetime, interviewEndDatetime, ...other } = interview
    return nullPropsToUndefined({
      ...other,
      interviewDate: fromApiYmd(interviewDate),
      interviewStartDatetime: fromApiYmdHms(interviewStartDatetime),
      interviewEndDatetime: fromApiYmdHms(interviewEndDatetime),
    })
  })
}

const GetAcceptNotice = async (childId?: string) => {
  const response = await executeGetBatchResult({ 
      childId,
  });

  return response.result
};
